import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    type ReactNode,
} from 'react'
import { useAuth } from '@common/auth'

/**
 * Ability/permissions system — replaces AngularJS `ability` directive and service.
 *
 * Legacy flow (security.js → securityapi.getUser()):
 *   1. OIDC login completes → access_token stored
 *   2. GET /api/user called with APS-Token header
 *   3. Response contains abilities[] (array of {id,name}) + user profile
 *   4. Abilities stored for authorization checks throughout the app
 *
 * The OIDC token only contains basic claims (sub, amr, sid, auth_time, idp).
 * Abilities and user profile (firstName, lastName, etc.) come from /api/user.
 */

export interface UserProfile {
    id?: number
    username?: string
    firstName?: string
    lastName?: string
    email?: string
    isActive?: boolean
    previousLoginDate?: string
    clientIdentificationNumberPreference?: string
    clientCardDisplayTypePreference?: string
    disputeCaseDataSource?: string
}

interface AbilityContextType {
    abilities: string[]
    hasAbility: (ability: string) => boolean
    hasAnyAbility: (...abilities: string[]) => boolean
    hasAllAbilities: (...abilities: string[]) => boolean
    userProfile: UserProfile | null
    isProfileLoaded: boolean
}

const AbilityContext = createContext<AbilityContextType | undefined>(undefined)

interface AbilityProviderProps {
    children: ReactNode
}

export function AbilityProvider({ children }: AbilityProviderProps) {
    const { user, isAuthenticated, getAccessToken } = useAuth()
    const [abilityIds, setAbilityIds] = useState<string[]>([])
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
    const [isProfileLoaded, setIsProfileLoaded] = useState(false)

    useEffect(() => {
        if (!isAuthenticated || !user) {
            setAbilityIds([])
            setUserProfile(null)
            setIsProfileLoaded(false)
            return
        }

        const token = getAccessToken()
        if (!token) return

        let cancelled = false

        // Replaces legacy: security.loggedin() → securityapi.getUser(token)
        // The /api/user endpoint returns abilities + user profile data.
        const apiPath = import.meta.env.VITE_API_PATH || '/api'
        fetch(`${apiPath}/user`, {
            headers: {
                'APS-Token': token,
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
        })
            .then((res) => {
                if (!res.ok) throw new Error(`GET /api/user failed: ${res.status}`)
                return res.json()
            })
            .then((data) => {
                if (cancelled) return

                // Extract ability IDs — response has abilities: [{id, name}, ...]
                // Legacy uppercases all IDs for comparison (user.js:35)
                const abilities: string[] = (data.abilities || []).map(
                    (a: { id?: string; name?: string } | string) =>
                        typeof a === 'string' ? a : a.id || a.name || '',
                )
                setAbilityIds(abilities)

                setUserProfile({
                    id: data.id,
                    username: data.username,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    email: data.email,
                    isActive: data.isActive,
                    previousLoginDate: data.previousLoginDate,
                    clientIdentificationNumberPreference:
                        data.clientIdentificationNumberPreference,
                    clientCardDisplayTypePreference:
                        data.clientCardDisplayTypePreference,
                    disputeCaseDataSource: data.disputeCaseDataSource,
                })
                setIsProfileLoaded(true)
            })
            .catch((err) => {
                if (cancelled) return
                console.error('Failed to fetch user profile:', err)
                setIsProfileLoaded(true)
            })

        return () => {
            cancelled = true
        }
    }, [isAuthenticated, user, getAccessToken])

    // Case-insensitive ability check with special 'AUTHENTICATED' handling
    // (matches legacy user.js:79 — 'AUTHENTICATED' always returns true)
    const hasAbility = useCallback(
        (ability: string) => {
            if (!ability) return false
            if (ability.toUpperCase() === 'AUTHENTICATED') return isAuthenticated
            return abilityIds.some(
                (a) => String(a).toUpperCase() === ability.toUpperCase(),
            )
        },
        [abilityIds, isAuthenticated],
    )

    const hasAnyAbility = useCallback(
        (...requiredAbilities: string[]) =>
            requiredAbilities.some((a) => hasAbility(a)),
        [hasAbility],
    )

    const hasAllAbilities = useCallback(
        (...requiredAbilities: string[]) =>
            requiredAbilities.every((a) => hasAbility(a)),
        [hasAbility],
    )

    return (
        <AbilityContext.Provider
            value={{
                abilities: abilityIds,
                hasAbility,
                hasAnyAbility,
                hasAllAbilities,
                userProfile,
                isProfileLoaded,
            }}
        >
            {children}
        </AbilityContext.Provider>
    )
}

export function useAbility(): AbilityContextType {
    const context = useContext(AbilityContext)
    if (!context) {
        throw new Error('useAbility must be used within an AbilityProvider')
    }
    return context
}

/** Wrapper component — renders children only if user has the required ability */
interface RequireAbilityProps {
    ability?: string
    anyOf?: string[]
    allOf?: string[]
    children: ReactNode
    fallback?: ReactNode
}

export function RequireAbility({
    ability,
    anyOf,
    allOf,
    children,
    fallback = null,
}: RequireAbilityProps) {
    const { hasAbility, hasAnyAbility, hasAllAbilities } = useAbility()

    let allowed = true
    if (ability) allowed = hasAbility(ability)
    if (anyOf) allowed = hasAnyAbility(...anyOf)
    if (allOf) allowed = hasAllAbilities(...allOf)

    return allowed ? <>{children}</> : <>{fallback}</>
}
